import * as React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableSortLabel
} from '@mui/material';
import {UserActivityTypes} from "../../types";
import {When} from "react-if";
import NoData from "../no-data";
import {convertWeekYearsToDisplay} from "../../helpers/weekYearDisplayFormat";
import Loading from "../loading";

interface Props {
    loading?: boolean;
    data: UserActivityTypes.TeamUserWeeklyActivityRecords[]
    onSort?: (sortBy: string, sortOrder: 'ASC' | 'DESC') => void;
    currentSortBy?: string;
    currentSortOrder?: 'ASC' | 'DESC';
}

interface TabularRow {
    username: string;
    teamName: string;
    [weekYear: string]: string | number; // Dynamic columns for each week;
    percentDaysActive : number;
}

type SortableColumn = 'username' | 'teamName' | 'percentDaysActive';

const styles = {
    container: {
        borderRadius: 1,
        border: '0.1px solid #ccc',
        backgroundColor: 'white',
        padding: '3rem 1.5rem 3rem 1.5rem'
    }
};

export const TeamUserWeeklyDataTable : React.FC<Props>  = ({
                                                               data,
                                                               loading,
                                                               onSort,
                                                               currentSortBy = 'username',
                                                               currentSortOrder = 'ASC'
                                                           })=> {

    const handleSort = (column: string) => {
        if (!onSort) return;

        // Only allow sorting on specific columns
        const sortableColumns: SortableColumn[] = ['username', 'teamName', 'percentDaysActive'];
        if (!sortableColumns.includes(column as SortableColumn)) return;

        let newSortOrder: 'ASC' | 'DESC' = 'ASC';

        if (currentSortBy === column) {
            // Toggle sort order if clicking the same column
            newSortOrder = currentSortOrder === 'ASC' ? 'DESC' : 'ASC';
        } else {
            // Set new column and default to ascending
            newSortOrder = 'ASC';
        }

        onSort(column, newSortOrder);
    };

    // Get all unique weekYears from the data
    const weekYears = Array.from(
        new Set(
            data.flatMap(user => user.weeklyActivity.map(w => w.weekYear))
        )
    ).sort();

    // Define columns: first column is 'Team Name / User', then weekYears
    const columns: string[] = ['username', 'teamName', ...weekYears, 'percentDaysActive'];
    const displayCols: string[] = ['User Name / Work Week', 'Team Name', ...convertWeekYearsToDisplay(weekYears), '% of Days Active'];

    // Build rows
    const rows: TabularRow[] = data.map(user => {
        const row: TabularRow = {
            username: user.username,
            teamName: ` ${user.teamName}`,
            percentDaysActive: user.summary.activeDaysPercentage
        };
        weekYears.forEach(week => {
            const activity = user.weeklyActivity.find(w => w.weekYear === week);
            // You can customize the value shown here
            row[week] = activity ? activity.activeDaysInWeek : '-';
        });
        return row;
    });

    const isSortableColumn = (columnKey: string): boolean => {
        return ['username', 'teamName', 'percentDaysActive'].includes(columnKey);
    };

    return (
        <div style={{ width: '100%', height: 400, overflow: 'auto' }}>
            <TableContainer sx={{ borderRadius: 0, maxHeight: 400, overflow: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                <When condition={
                    !loading && (!data || Object.keys(data).length === 0)
                }>

                    <Box sx={{...styles.container}}>
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={'No data available'}
                            description={
                                'There are no user activity records available'
                            }
                        />
                    </Box>
                </When>
                <When condition={ data && Object.keys(data).length > 0}>
                    <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow sx={{borderBottom: '4px solid white' }} key={'header-row'}>
                                {displayCols.map((column, i) => {
                                    const columnKey = columns[i];
                                    const isLastColumn = i === displayCols.length - 1;

                                    return (
                                        <TableCell
                                            sx={{
                                                backgroundColor: '#BDBABA !important',
                                                backgroundImage: 'none',
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 1,
                                                padding: '8px 16px'
                                            }}
                                            key={i}
                                            align={i > 1 ? 'right' : 'inherit'}
                                        >
                                            {isSortableColumn(columnKey) && onSort ? (
                                                <TableSortLabel
                                                    active={currentSortBy === columnKey}
                                                    direction={currentSortBy === columnKey ? currentSortOrder.toLowerCase() as 'asc' | 'desc' : 'asc'}
                                                    onClick={!loading ? () => handleSort(columnKey) : undefined}
                                                    sx={{
                                                        '& .MuiTableSortLabel-root': {
                                                            color: '#000000',
                                                            fontWeight: 700,
                                                            fontSize: 14
                                                        },
                                                        '& .MuiTableSortLabel-root:hover': {
                                                            color: '#333333'
                                                        },
                                                        '& .MuiTableSortLabel-icon': {
                                                            color: '#000000 !important'
                                                        }
                                                    }}
                                                >
                                                    <Typography color={'#000000'} fontSize={14} fontWeight={700}>
                                                        {column}
                                                    </Typography>
                                                </TableSortLabel>
                                            ) : (
                                                <Typography color={'#000000'} fontSize={14} fontWeight={700}>
                                                    {column}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <When condition={!loading}>
                            <TableBody>
                                {rows.map((row,index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            borderBottom: '4px solid white',
                                            '&:nth-of-type(even)': {
                                                backgroundColor: '#F5F4F4'
                                            },
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: '#DDF5FF'
                                            },
                                            ":hover": {
                                                backgroundColor: '#dbd8d8',
                                                cursor: 'pointer'
                                            }
                                        }}
                                    >
                                        {columns.map((col, i) => (
                                            <TableCell key={i} align={i > 1 ? 'center' : 'inherit'}>
                                                {col === 'percentDaysActive' ? `${Math.round(row.percentDaysActive)}%` : (col === 'username' ? row[col]?.replaceAll("null",' ') : row[col])}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </When>
                    </Table>
                </When>
                <When condition={loading}>
                    <Loading />
                </When>
            </TableContainer>
        </div>
    );
}